// TODO Type 'CalendarDate' is not assignable to type 'DateValue | null | undefined'.
// @ts-nocheck
import { Button as SharedButton } from '@florencelabs/button';
import { twMerge } from '@florencelabs/fabric-styles';
import { MaterialSymbol } from '@florencelabs/icons';
import { ModalWithTrigger } from '@florencelabs/modal-2';
import { DateValue } from '@internationalized/date';
import clsx from 'clsx';
import { ReactElement, useMemo } from 'react';
import {
  Button,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  I18nProvider,
} from 'react-aria-components';
import { useToggle } from 'usehooks-ts';

import { DAYS_RANGE } from '@provider-directory/constants';
import { ISearchCalendarProps } from '@provider-directory/hooks/useCalendar';
import {
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarHeading,
  CalendarHeadingButton,
} from '../date-picker/calendar.component';
import { CalendarButtonDrawer } from './calendar-button-drawer';
import { CustomCalendarError } from './custom-calendar-error';

const ADD_DAY = 1;
interface IControlledCalendarDrawerProps
  extends Omit<ISearchCalendarProps, 'isDesktop' | 'setPreviousRange'> {
  cancelCta: string;
  applyCta: string;
}
const ControlledCalendarDrawer = ({
  isOpen,
  locale,
  previousRange,
  value,
  t,
  timeZone,
  todaysDate,
  toggleOpen,
  setIsOpen,
  timeStampHandler,
  lastFetchableDate,
  onChange,
}: IControlledCalendarDrawerProps): ReactElement => {
  const [isOpenDrawerError, toogleOpenDrawerError] = useToggle(false);
  const [isOpenDrawer, toogleDrawer] = useToggle(false);
  const handleNextClickHandler = (startDate: DateValue, endDate: DateValue) => {
    onChange({
      startDate,
      endDate,
      shouldSetPreviousRange: true,
    });
  };
  const nextClickHandler = () => {
    const currentDate = value.startDate;
    if (currentDate) {
      let newStartDate = currentDate.add({
        days: ADD_DAY,
      });
      const comparison = lastFetchableDate.compare(newStartDate);
      if (comparison === -1) {
        toogleDrawer();
        return;
      }
      if (comparison <= 0) {
        newStartDate = newStartDate.subtract({ days: Math.abs(comparison) });
      }
      handleNextClickHandler(
        newStartDate,
        newStartDate.add({ days: DAYS_RANGE }),
      );
    }
  };
  const handlePreviousClickHandler = (
    startDate: DateValue,
    endDate: DateValue,
  ) => {
    onChange({
      startDate,
      endDate,
      shouldSetPreviousRange: true,
    });
  };
  const previousClickHandler = () => {
    const currentDate = value.startDate;
    if (currentDate) {
      let newStartDate = currentDate.subtract({
        days: ADD_DAY,
      });
      let newEndDate = newStartDate.add({
        days: DAYS_RANGE,
      });
      const comparison = newStartDate.compare(todaysDate);
      if (comparison < 0) {
        newStartDate = newStartDate.add({ days: Math.abs(comparison) });
        newEndDate = newEndDate.add({ days: DAYS_RANGE });
      }
      handlePreviousClickHandler(newStartDate, newEndDate);
    }
  };
  const handleCancelAndOutsideInteraction = () => {
    if (
      isOpen &&
      value.startDate &&
      previousRange?.startDate &&
      previousRange.endDate
    ) {
      onChange({
        startDate: previousRange.startDate,
        endDate: previousRange.endDate,
        shouldSetPreviousRange: true,
      });
    }
  };

  const handleApply = () => {
    if (value.startDate) {
      onChange({
        startDate: value.startDate,
        endDate: value.startDate.add({ days: DAYS_RANGE }),
        shouldSetPreviousRange: true,
      });
    }
  };

  const disabledNextButton = useMemo(() => {
    if (value.endDate && value.startDate) {
      const comparisonLastDate = lastFetchableDate.compare(value.startDate);
      return comparisonLastDate <= 0;
    }
    return true;
  }, [lastFetchableDate, value.endDate, value.startDate]);

  const disabledPrevButton = useMemo(() => {
    if (value.startDate && value.endDate) {
      const comparisonTodaysDate = value.startDate.compare(todaysDate);
      return comparisonTodaysDate <= 0;
    }
    return true;
  }, [value.startDate, todaysDate, value.endDate]);

  return (
    <div
      data-testid={'drawer-calendar'}
      className={'col-span-2 text-red-500 desktop:hidden'}
    >
      <ModalWithTrigger
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        shouldCloseOnInteractOutside={(_) => {
          handleCancelAndOutsideInteraction();
          return true;
        }}
        trigger={
          <div className={'flex items-center rounded-md bg-white px-4 py-3'}>
            <Button
              slot={null}
              className={twMerge(
                'flex w-fit items-center justify-center border-0 p-1 text-gray-950',
                {
                  'text-gray-950/30': disabledPrevButton,
                },
              )}
              onPress={previousClickHandler}
            >
              <MaterialSymbol
                name={'chevron_left'}
                aria-hidden={true}
                size={20}
              />
            </Button>

            <Button
              slot={null}
              aria-hidden={true}
              onPress={toggleOpen}
              className={clsx(
                'flex flex-1 items-center justify-center text-gray-700 hover:text-primary-600',
              )}
            >
              <div
                className={
                  'flex flex-row items-center justify-between gap-x-3 desktop:items-end'
                }
              >
                <MaterialSymbol
                  name={'calendar_today'}
                  aria-hidden={true}
                  size={20}
                  className={'text-gray-950'}
                />
                {value.startDate ? (
                  <span className={'text-body-small-semibold text-gray-950'}>
                    {timeStampHandler(value.startDate)}
                  </span>
                ) : null}
              </div>
            </Button>
            <CustomCalendarError
              label={t('searchResults.appointmentTitle')}
              cta={t('searchResults.appointmentText')}
              isOpen={isOpenDrawer}
              toggleModal={() => {
                toogleDrawer();
              }}
            >
              <Button
                slot={null}
                className={twMerge(
                  'flex w-fit items-center justify-center border-0 p-1 text-gray-950',
                  {
                    'text-gray-950/30': disabledNextButton,
                  },
                )}
                onPress={nextClickHandler}
              >
                <MaterialSymbol
                  name={'chevron_right'}
                  aria-hidden={true}
                  size={20}
                />
              </Button>
            </CustomCalendarError>
          </div>
        }
        variant={'bottomDrawer'}
        modalClassName={'rounded-t-lg h-fit w-full'}
        dialogClassName={'px-0 pt-8 pb-0 h-fit'}
        overlayClassName={'bg-black/20 h-dvh'}
      >
        <>
          <I18nProvider locale={locale}>
            <div className={'flex flex-col items-center'}>
              <>
                <span className={'sr-only'} id={'slotDate-drawer-label'}>
                  {t('appointmentSlot.appointmentType.label')}
                </span>
                <Calendar
                  aria-labelledby={'slotDate-drawer-label'}
                  value={value.startDate}
                  onChange={(dateValue) => {
                    onChange({
                      startDate: dateValue,
                      endDate: dateValue.add({ days: DAYS_RANGE }),
                    });
                  }}
                  minValue={todaysDate}
                  maxValue={lastFetchableDate}
                  className={'w-full px-6 tablet:px-12'}
                >
                  <div className={'mb-6 flex items-center justify-between'}>
                    <CalendarHeading className={'px-3 tablet:px-6'} />
                    <div className={'flex items-center justify-center gap-x-6'}>
                      <CalendarHeadingButton slot={'previous'} />
                      <CalendarButtonDrawer
                        toogleOpenDrawer={toogleOpenDrawerError}
                        isOpenDrawerError={isOpenDrawerError}
                        lastFetchableDate={lastFetchableDate}
                      />
                    </div>
                  </div>
                  <CalendarGrid className={'min-h-fit'}>
                    <CalendarGridHeader>
                      {(day) => (
                        <CalendarHeaderCell
                          className={
                            'w-fit text-center text-body-medium-medium'
                          }
                        >
                          {day}
                        </CalendarHeaderCell>
                      )}
                    </CalendarGridHeader>
                    <CalendarGridBody>
                      {(date) => (
                        <CalendarCell date={date} timeZone={timeZone} />
                      )}
                    </CalendarGridBody>
                  </CalendarGrid>
                </Calendar>
                <div
                  className={
                    'flex w-full items-center justify-end gap-6 border-t border-t-gray-400 bg-white px-6 py-4'
                  }
                >
                  <SharedButton
                    variant={'systemLink'}
                    className={'outline-none'}
                    onPress={() => {
                      handleCancelAndOutsideInteraction();
                      toggleOpen();
                    }}
                  >
                    {t('search.cancel')}
                  </SharedButton>

                  <SharedButton
                    className={'!bg-[#153865] !text-white'}
                    variant={'clientMain'}
                    type={'button'}
                    onPress={() => {
                      toggleOpen();
                      handleApply();
                    }}
                  >
                    {t('search.apply')}
                  </SharedButton>
                </div>
              </>
            </div>
          </I18nProvider>
        </>
      </ModalWithTrigger>
    </div>
  );
};

export default ControlledCalendarDrawer;
